/**
 * Idle session expiry — a control Firebase does not provide.
 *
 * Firebase keeps a long-lived refresh token in the browser and never expires a
 * session on its own: sign in once and the app stays open until an explicit
 * sign-out. On a shared or unattended machine that is a standing key. This adds
 * the missing timeout — a stretch of no interaction signs the user out, and
 * reopening the app after that stretch lands on the sign-in screen too, because
 * the last-activity time is checked on load as well as while running.
 *
 * The *decision* lives here as a pure function so it can be tested without a
 * browser or a clock. The wiring — listeners, interval, sign-out — lives in
 * `AuthProvider`, which is the only place that can actually end a session.
 */

/** Where the last-activity timestamp is kept. Namespaced to avoid collisions. */
const STORAGE_KEY = 'budgetmonkey:lastActivity'

/**
 * How long a session may sit idle before it ends.
 *
 * Eight hours: long enough not to interrupt a day of intermittent use — this
 * app is worked in bursts, standing at a filing cabinet — but short enough that
 * a session left open overnight is gone by morning. A stricter value is a
 * one-line change here; the device's own lock is the faster-acting control for
 * the walked-away-for-ten-minutes case.
 */
export const IDLE_LIMIT_MS = 8 * 60 * 60 * 1000

/**
 * Whether a session has been idle past the limit.
 *
 * A missing timestamp is treated as *not* expired: it means either a brand-new
 * sign-in, before any activity has been recorded, or storage that was cleared —
 * and clearing storage already drops the session, so there is nothing to expire.
 * Uses `>`, so a session exactly at the limit is still live; expiry is strictly
 * past it.
 */
export function idleExpired(
  lastActivity: number | null,
  now: number,
  limit: number = IDLE_LIMIT_MS,
): boolean {
  if (lastActivity === null) return false
  return now - lastActivity > limit
}

/**
 * The storage the wiring reads and writes. An interface so a test can supply an
 * in-memory one and never touch `localStorage`.
 */
export interface ActivityStore {
  read(): number | null
  write(at: number): void
  clear(): void
}

/**
 * The decision a signed-in session makes the moment it mounts.
 *
 * Read the stored time and judge it *before* recording anything. A session
 * reopened after the idle window arrives with a stale stamp from last time, and
 * writing "now" first would erase the evidence the check depends on — which is
 * exactly the bug this ordering exists to prevent. Extracted from the wiring so
 * the order can be tested without a browser: a version that writes before it
 * checks fails the stale-stamp test here rather than only in a live reload.
 *
 * When live, records fresh activity and returns not-expired. When expired,
 * clears the stamp and returns expired, and the caller signs out.
 */
export function resolveSessionOnMount(
  store: ActivityStore,
  now: number,
): { expired: boolean } {
  if (idleExpired(store.read(), now)) {
    store.clear()
    return { expired: true }
  }
  store.write(now)
  return { expired: false }
}

/** The real store. Guarded so a missing `localStorage` degrades to no-op. */
export const localActivityStore: ActivityStore = {
  read() {
    if (typeof localStorage === 'undefined') return null
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw === null) return null
    const parsed = Number(raw)
    return Number.isFinite(parsed) ? parsed : null
  },
  write(at) {
    if (typeof localStorage === 'undefined') return
    localStorage.setItem(STORAGE_KEY, String(at))
  },
  clear() {
    if (typeof localStorage === 'undefined') return
    localStorage.removeItem(STORAGE_KEY)
  },
}
